import { useCallback, useEffect, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Dialog } from '@factorialco/f0-react'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { useBulkImportCertificates } from 'modules/trainings/hooks/TrainingMembershipFiles/useBulkImportCertificates'

import { ParticipantInfo } from './types'
import { ParticipantFileRow } from './ParticipantFileRow'
import { useDirectUploadPerParticipant } from './useDirectUploadPerParticipant'

interface ImportCertificatesProps {
  onClose: () => void
  trainingId: number
  participants: ParticipantInfo[]
}

export const ImportCertificates = ({
  onClose,
  trainingId,
  participants,
}: ImportCertificatesProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { bulkImportCertificates } = useBulkImportCertificates()

  const {
    participantFilesMap,
    initializeParticipants,
    uploadFiles,
    removeFile,
    reset,
    hasUploadsInProgress,
    hasAnyFiles,
    hasErrors,
    getMembershipsWithSignedIds,
  } = useDirectUploadPerParticipant({ trainingId })

  // Initialize participants when modal opens or participants change
  useEffect(() => {
    initializeParticipants(participants)

    return () => {
      reset()
    }
  }, [participants, initializeParticipants, reset])

  const handleFileDrop = useCallback(
    (membershipId: number, files: File[]) => {
      uploadFiles(membershipId, files)
    },
    [uploadFiles]
  )

  const handleFileRemove = useCallback(
    (membershipId: number, fileId: string) => {
      removeFile(membershipId, fileId)
    },
    [removeFile]
  )

  const handleImport = useCallback(async () => {
    const membershipsWithSignedIds = getMembershipsWithSignedIds()

    if (membershipsWithSignedIds.length === 0) {
      return
    }

    setIsSubmitting(true)

    const result = await bulkImportCertificates({
      trainingId,
      membershipsWithFiles: membershipsWithSignedIds,
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.import_certificates_error'
        ),
      })
    })

    setIsSubmitting(false)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.import_certificates_success'
        ),
      })
      onClose()
    }
  }, [getMembershipsWithSignedIds, bulkImportCertificates, trainingId, onClose])

  const handleClose = useCallback(() => {
    reset()
    onClose()
  }, [reset, onClose])

  const canImport = hasAnyFiles && !hasUploadsInProgress && !isSubmitting

  const isLoading = hasUploadsInProgress || isSubmitting

  // Convert map to array for rendering
  const participantsArray = Array.from(participantFilesMap.values())

  return (
    <F0Dialog
      isOpen
      onClose={handleClose}
      title={i18n.t(
        'trainings.trainings_revamp.training_detail.employees_table.import_certificates'
      )}
      primaryAction={{
        label: i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.import_certificates'
        ),
        onClick: handleImport,
        disabled: !canImport,
        loading: isLoading,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: handleClose,
      }}
    >
      <Box gap="s24">
        <F0Alert
          title={i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.import_certificates_instructions_title'
          )}
          variant="info"
          description={i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.import_certificates_instructions_description'
          )}
        />

        <Box gap="s8">
          {participantsArray.map((participant) => (
            <ParticipantFileRow
              key={participant.membershipId}
              participant={participant}
              onFileDrop={handleFileDrop}
              onFileRemove={handleFileRemove}
            />
          ))}
        </Box>

        {hasErrors && (
          <F0Alert
            title={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.import_certificates_errors'
            )}
            variant="warning"
            description={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.import_certificates_errors_description'
            )}
          />
        )}
      </Box>
    </F0Dialog>
  )
}
